import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';

export const Route = createFileRoute('/carbon-methodology')({
  component: CarbonMethodologyPage,
});

/**
 * Static methodology page, mirrored from `wiki/09-product/carbon-methodology.md`
 * (SIL-352). The two must stay in sync — hardcoded as JSX (no markdown
 * renderer is a dependency of this app) rather than importing the markdown
 * file, so any edit to the wiki page needs this file updated too.
 *
 * This page is behind the customer portal's normal Cognito auth gate, same
 * as every other route (including `/privacy`) — it is NOT reachable without
 * an account. That is a known, deliberate limitation (see SIL-352 comments)
 * for anyone relying on it as unauthenticated public substantiation.
 */

const TH = 'border-b border-zinc-200 bg-zinc-50 px-3 py-2 text-left font-semibold text-zinc-700';
const TD = 'border-b border-zinc-100 px-3 py-2 align-top text-zinc-700';
const TABLE_WRAP = 'mt-3 overflow-x-auto';
const TABLE = 'w-full border-collapse text-sm';
const CODE = 'rounded bg-zinc-100 px-1 py-0.5 font-mono text-[13px] text-zinc-800';
const QUOTE = 'mt-2 border-l-2 border-zinc-300 pl-3 text-zinc-600';
const LINK = 'text-brand-600 underline';

function Ext({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={LINK}>
      {children}
    </a>
  );
}

function Int({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} className={LINK}>
      {children}
    </a>
  );
}

function CarbonMethodologyPage() {
  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6">
        <PageHeader
          title="Carbon Footprint Methodology"
          subtitle="How SiliconX computes embodied and use-phase carbon estimates for hardware it lists or tracks, the assumptions behind avoided-emissions claims, and the limits of those numbers."
        />
      </div>

      <Card className="space-y-6 text-sm text-foreground">
        <p>SiliconX reports two structurally different carbon figures:</p>
        <ul className="ml-5 list-disc space-y-1">
          <li>
            <strong>Embodied carbon</strong> — Embodied carbon is the CO2 emitted making this
            hardware - manufacturing, shipping, and end-of-life disposal - not from running it.
            Figures model a realistic production configuration, not the manufacturer&apos;s
            minimum test configuration, so they may exceed a vendor datasheet number. Computed{' '}
            <strong>per SKU</strong> (catalog grain).
          </li>
          <li>
            <strong>Use-phase (operational) carbon</strong> — the CO2 emitted <em>running</em> an
            owned asset, as an <strong>annual rate</strong>. Computed <strong>per asset</strong>{' '}
            (fleet grain), for <code className={CODE}>server</code>-class assets only.
          </li>
        </ul>
        <p>
          These are two independently-scoped figures, reported in separate columns everywhere
          they appear, and must never be summed.
        </p>
        <p>
          This page is the substantiation trail for every carbon figure SiliconX shows - the
          listing tooltip, the digital passport, the fleet CSV export, the asset detail pages, and
          financial scenarios all link here. It states what we compute, how, with which tool and
          version, what we assume, and what we deliberately do not claim.
        </p>

        <section>
          <h2 id="summary" className="text-base font-semibold text-foreground">
            Summary
          </h2>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>Embodied carbon</strong> (manufacturing + transport + end-of-life) is
              estimated <strong>per SKU</strong> using <strong>BoaviztAPI 2.3.0</strong>, an
              open-source methodology maintained by the Boavizta community.
            </li>
            <li>
              <strong>Use-phase carbon</strong> (operational electricity) is estimated{' '}
              <strong>per asset</strong>, also via BoaviztAPI, as an <strong>annual rate in kg
              CO2e/yr</strong> - never a cumulative or lifetime total. It is labeled{' '}
              <strong>Scope 2, location-based</strong>; no market-based figure is provided (see{' '}
              <Int href="#scope">Scope</Int> below).
            </li>
            <li>
              {/* SIL-379 shipped org-level PUE; SIL-356 tracks per-facility PUE (remaining scope). */}
              Every use-phase figure was, until recently, a <strong>screening estimate</strong> -
              no asset could qualify for the top accuracy tier, because no org- or facility-level
              PUE data existed. An org can now supply its own PUE (
              <code className={CODE}>customer_org.pue</code>); per-facility PUE is still missing.
              See <Int href="#accuracy-tiers-use-phase">Accuracy tiers</Int>.
            </li>
            <li>
              The listing claim - buying refurbished <strong>can avoid</strong> the embodied
              emissions of manufacturing a new equivalent-spec unit - is an{' '}
              <strong>assumption, not a measurement</strong>.
            </li>
            <li>
              Every embodied figure carries min-max uncertainty bounds; every use-phase figure
              carries a bounds pair that means something narrower (observed day-to-day power
              variation only - see <Int href="#bounds-use-phase">Bounds</Int>).
            </li>
            <li>
              Every estimate, of either kind, is stored in an append-only snapshot with its full
              inputs for reproducibility.
            </li>
            <li>
              These are estimates, not audits. No third-party assurance has been obtained.{' '}
              <strong>Classification against GHG Protocol categories is the customer&apos;s own
              determination</strong> - see{' '}
              <Int href="#classification-is-the-customers-determination">Classification</Int>.
            </li>
          </ul>
        </section>

        <section>
          <h2 id="methodology-and-attribution" className="text-base font-semibold text-foreground">
            Methodology and attribution
          </h2>
          <p className="mt-2">
            SiliconX&apos;s carbon figures - both embodied and use-phase - are computed with{' '}
            <Ext href="https://github.com/Boavizta/boaviztapi">BoaviztAPI</Ext>, an open-source
            environmental-footprint methodology and API developed and maintained by the{' '}
            <Ext href="https://boavizta.org">Boavizta</Ext> working group, a community of
            organizations collaborating on the measurement of ICT environmental impacts.
            BoaviztAPI is published under the AGPL-3.0 license. SiliconX runs it as an{' '}
            <strong>unmodified container</strong> inside its own offline batch pipeline; the
            software is never distributed to customers.
          </p>
          <p className="mt-2">
            Attribution: <em>BoaviztAPI, by the Boavizta working group -{' '}
            <Ext href="https://github.com/Boavizta/boaviztapi">
              https://github.com/Boavizta/boaviztapi
            </Ext>{' '}
            (AGPL-3.0).</em> The project publishes no preferred citation file; this attribution
            names the project, its maintainers, and the source repository directly.
          </p>

          <h3 id="pinned-version" className="mt-4 text-sm font-semibold text-foreground">
            Pinned version
          </h3>
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH}>Item</th>
                  <th className={TH}>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={TD}>Docker image</td>
                  <td className={TD}>
                    <code className={CODE}>ghcr.io/boavizta/boaviztapi:2.3.0</code>
                  </td>
                </tr>
                <tr>
                  <td className={TD}>API-reported version</td>
                  <td className={TD}>
                    <code className={CODE}>2.3.0</code>
                  </td>
                </tr>
                <tr>
                  <td className={TD}>Recorded per snapshot</td>
                  <td className={TD}>
                    <code className={CODE}>
                      methodology_version = boaviztapi:docker-2.3.0;api-2.3.0
                    </code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2">
            The version is pinned and recorded on every snapshot - embodied (
            <code className={CODE}>sku_carbon_footprint</code>) and use-phase (
            <code className={CODE}>asset_use_phase_snapshot</code>) alike - so any figure can be
            traced to the exact methodology release that produced it.
          </p>
        </section>

        <section>
          <h2 id="scope" className="text-base font-semibold text-foreground">
            Scope
          </h2>

          <h3 id="embodied" className="mt-4 text-sm font-semibold text-foreground">
            Embodied
          </h3>
          <p className="mt-2">
            Estimates cover <strong>embodied carbon only</strong>: manufacturing, transport, and
            end-of-life. This scope is unchanged.
          </p>

          <h3 id="use-phase" className="mt-4 text-sm font-semibold text-foreground">
            Use-phase
          </h3>
          <p className="mt-2">
            Use-phase covers <strong>operational electricity consumption</strong>, computed per
            asset from telemetry where it exists and from an archetype default where it does not
            (see <Int href="#how-a-use-phase-figure-is-computed">Use-phase computation</Int>{' '}
            below). It is <strong>not</strong> a lifetime or cumulative figure - the stored
            primitive is an annual rate (kg CO2e/yr), and every surface that renders it makes the{' '}
            <code className={CODE}>/yr</code> unmissable in the same label.
          </p>
          <p className="mt-2">
            <strong>Labeled Scope 2, location-based, with no market-based figure.</strong>{' '}
            CSRD/ESRS E1-6 expects both a location-based and a market-based Scope 2 figure.
            SiliconX provides <strong>only</strong> the location-based figure. A market-based
            figure requires the customer&apos;s own contractual REC/GO (Renewable Energy
            Certificate / Guarantee of Origin) instruments, which SiliconX does not hold and
            cannot compute on a customer&apos;s behalf. Every surface that carries the{' '}
            <code className={CODE}>Scope 2 (location-based)</code> label must also carry (or
            link) this no-market-based statement - see{' '}
            <code className={CODE}>USE_PHASE_SCOPE_LABEL</code> /{' '}
            <code className={CODE}>USE_PHASE_NO_MARKET_BASED_STATEMENT</code>.
          </p>

          <h3
            id="classification-is-the-customers-determination"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            Classification is the customer&apos;s determination
          </h3>
          <p className="mt-2">
            This came out of a regulatory review and is not optional: SiliconX computes figures to{' '}
            <strong>support</strong> GHG inventory reporting, but the correct GHG Protocol
            classification for any given customer depends on that customer&apos;s organizational
            boundary, consolidation approach, and capitalization policy. SiliconX does not assert
            a flat category mapping for every customer.
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>Embodied carbon</strong> is typically Scope 3 <strong>Category 1</strong>{' '}
              (Purchased Goods and Services) <em>or</em> Scope 3 <strong>Category 2</strong>{' '}
              (Capital Goods), depending on whether the customer capitalizes the asset - and
              applies <strong>only in the period of acquisition</strong>, not every year the
              asset is held.
            </li>
            <li>
              <strong>Use-phase carbon</strong> is Scope 2 <strong>only where the customer
              directly purchases the electricity</strong> for the asset. For assets in colocation
              or operated by a third party, the emissions are generally <strong>Scope 3</strong>{' '}
              (for example, Category 8 - Upstream Leased Assets) instead, and the PUE overhead
              component included in the figure may not belong in the customer&apos;s Scope 2
              inventory at all.
            </li>
            <li>
              The <code className={CODE}>ghg_protocol_category</code> CSV column states the{' '}
              <strong>common case only</strong> - it is frozen, scoped to the{' '}
              <code className={CODE}>gwp_embodied_*</code> columns, and is <strong>not</strong> a
              determination for any given organization. The use-phase Scope 2 label lives in a
              separate <code className={CODE}>use_phase_ghg_category</code> column so the two can
              never be summed by an analyst treating &quot;the GHG category column&quot; as one
              field.
            </li>
          </ul>
        </section>

        <section>
          <h2
            id="how-an-embodied-figure-is-computed"
            className="text-base font-semibold text-foreground"
          >
            How an embodied figure is computed
          </h2>
          <p className="mt-2">
            <strong>Servers</strong> are computed via BoaviztAPI&apos;s server model from the
            SKU&apos;s configuration.
          </p>
          <p className="mt-2">
            <strong>GPU servers</strong> are computed as the server configuration{' '}
            <strong>plus N × GPU component impacts, summed</strong> - BoaviztAPI&apos;s server
            model has no GPU component for embodied (manufacturing) impact, so GPU impacts are
            resolved separately and added. GPU name resolution is guarded: cross-family fuzzy
            matches are rejected, and unknown GPUs fall back to BoaviztAPI&apos;s default GPU
            archetype (version 2.3.0 ships parameters for 8 GPU models).
          </p>

          <h3 id="inputs-and-defaults" className="mt-4 text-sm font-semibold text-foreground">
            Inputs and defaults
          </h3>
          <p className="mt-2">
            Inputs are derived from each SKU&apos;s active OEM specification and class
            attributes. Where a field is unavailable, BoaviztAPI archetype defaults are used
            instead, and every defaulted field is recorded per snapshot in{' '}
            <code className={CODE}>defaulted_fields</code>. Every figure carries{' '}
            <strong>min-max uncertainty bounds that widen as more fields are defaulted</strong>.
          </p>
          <p className="mt-2">
            {/* SIL-395 */}
            <strong>Installed RAM</strong> is no longer always archetype-defaulted.
            Because RAM drives ~88-90% of a server&apos;s embodied GWP, feeding the OEM
            spec&apos;s <code className={CODE}>max_ram_gb</code> straight through - a{' '}
            <strong>maximum supported</strong> ceiling, not an installed configuration - would
            overstate embodied carbon by an order of magnitude. Instead the deriver assumes a
            realistic installed fraction of that ceiling (25%, capped at 6,144 GB), sized into
            DIMM modules capped by the SKU&apos;s scraped <code className={CODE}>dimm_slots</code>{' '}
            count (falling back to an assumed 32-slot chassis where no slot count is on record),
            and records exactly how the figure was reached - estimated from scraped slots,
            estimated from an assumed slot count, or the BoaviztAPI archetype default when the
            SKU has no <code className={CODE}>max_ram_gb</code> signal at all - in{' '}
            <code className={CODE}>defaulted_fields[&apos;ram&apos;]</code>, never presented as a
            measured value. This raised the median server SKU&apos;s modeled RAM configuration
            from the old flat 256 GB archetype assumption to roughly 1,024 GB. Installed disk
            remains archetype-defaulted (no per-SKU storage configuration data exists).
          </p>

          <h3
            id="unsupported-asset-classes-embodied"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            Unsupported asset classes (embodied)
          </h3>
          <p className="mt-2">
            Asset classes without a sound model in this pipeline -{' '}
            <code className={CODE}>cpu</code>, <code className={CODE}>cloud_vm</code>,{' '}
            <code className={CODE}>rack_system</code>, <code className={CODE}>storage</code>,{' '}
            <code className={CODE}>ib_switch</code> - have <strong>no embodied footprint
            computed</strong>. They are shown as pending or absent, never estimated from a
            generic archetype.
          </p>
        </section>

        <section>
          <h2
            id="how-a-use-phase-figure-is-computed"
            className="text-base font-semibold text-foreground"
          >
            How a use-phase figure is computed
          </h2>
          <p className="mt-2">
            Use-phase is computed monthly by a separate batch CLI (
            <code className={CODE}>pnpm carbon:compute-use-phase</code>,{' '}
            <code className={CODE}>apps/ingestion</code>), via BoaviztAPI&apos;s{' '}
            <code className={CODE}>/v1/server/</code> endpoint, reading{' '}
            <code className={CODE}>gwp.use</code> scoped to exactly one year via the{' '}
            <code className={CODE}>?duration=8760</code> query parameter. Engineering mechanics
            (idempotency, selection query, hashing, grant preflight) are documented in{' '}
            <Ext href="/04-ingestion/carbon-computation#use-phase-operational-carbon-computation">
              Use-Phase Carbon Computation
            </Ext>
            ; this section covers what the figure means and what it does not.
          </p>
          <p className="mt-2">
            <strong>Note on the decision this overturns.</strong> The embodied pipeline&apos;s
            rule is &quot;no SKU-grain defaults for an asset class with no sound model&quot; (see{' '}
            <Int href="#unsupported-asset-classes-embodied">Unsupported asset classes</Int>{' '}
            above). Use-phase overturns that rule <strong>for use-phase only</strong>: every
            in-scope asset gets a figure, computed from telemetry where it exists and from an
            archetype default where it does not. Embodied&apos;s stricter rule is unchanged.
          </p>

          <h3 id="coverage" className="mt-4 text-sm font-semibold text-foreground">
            Coverage
          </h3>
          <p className="mt-2">
            Only the <code className={CODE}>server</code> asset class is supported, which{' '}
            <strong>includes GPU servers</strong> - there is no separate{' '}
            <code className={CODE}>gpu_server</code> asset class in the schema; a GPU server is a{' '}
            <code className={CODE}>server</code> whose class attributes describe GPU slots.
          </p>
          <p className="mt-2">
            A <strong>bare <code className={CODE}>gpu</code>-class asset gets no use-phase
            figure, ever</strong> - this is a permanent{' '}
            <code className={CODE}>unsupported_asset_class</code>, not a gap awaiting a future
            computation. A loose accelerator sitting in inventory is not installed in a chassis
            and draws no operational power; there is nothing to model.
          </p>
          <p className="mt-2">
            <strong>GPU servers on the no-telemetry (archetype) path used to substantially
            understate their real draw</strong> - <code className={CODE}>/v1/server/</code>
            &apos;s archetype model has no GPU draw component in it at all, it was built for
            GPU-less servers. As of 2026-07-30, the archetype path now folds a
            TDP-derived accelerator power figure into the request for the accelerator models
            covered by the table below - see{' '}
            <Int href="#accelerator-power-on-the-archetype-path-sil-388-389-2026-07-30">
              Accelerator power on the archetype path
            </Int>{' '}
            for what changed and what is still disclosed as an understatement. Once telemetry
            exists, measured whole-system watts already include GPU draw at the BMC level and
            none of this applies - see the double-counting note below.
          </p>

          <h3 id="power-resolution" className="mt-4 text-sm font-semibold text-foreground">
            Power resolution
          </h3>
          <p className="mt-2">Two rungs, in order:</p>
          <ol className="ml-5 mt-2 list-decimal space-y-1">
            <li>
              <strong>Measured</strong> -{' '}
              <code className={CODE}>telemetry_daily_agg.avg_power_draw_watts</code>, averaged
              over the compute period&apos;s calendar window (
              <code className={CODE}>power_basis = &apos;telemetry_measured&apos;</code>).
            </li>
            <li>
              <strong>Archetype default</strong> - BoaviztAPI&apos;s own archetype-derived wattage
              for the resolved configuration, when no telemetry exists (
              <code className={CODE}>power_basis = &apos;archetype_default&apos;</code>),{' '}
              <strong>plus</strong> a TDP-derived accelerator power addition for GPU-bearing
              servers whose accelerator model is covered by the table below.
            </li>
          </ol>
          <p className="mt-2">
            There is still no <strong>whole-system</strong> TDP-derived rung, and that rejection
            is unchanged: <code className={CODE}>sku_oem_spec.tdp_watts</code>-as-chassis-power
            would misrepresent one accelerator&apos;s nameplate figure as an entire server&apos;s
            draw, ignoring CPU, RAM, disk, fans, and PSU losses entirely. What changed below
            is narrower and different in kind - see the next section.
          </p>

          <h3
            id="accelerator-power-on-the-archetype-path-sil-388-389-2026-07-30"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            {/* SIL-388/389 */}
            Accelerator power on the archetype path (2026-07-30)
          </h3>
          <p className="mt-2">
            <strong>The rejection above is about a whole-<em>system</em> TDP stand-in. It does
            not apply to a per-<em>component</em> accelerator power supplied to BoaviztAPI&apos;s
            own GPU-in-server model</strong> - a different claim, argued separately here.
          </p>
          <p className="mt-2">
            <strong>Why supplying our own figure is upstream-sanctioned, not invented.</strong>{' '}
            BoaviztAPI 2.3.0&apos;s{' '}
            <code className={CODE}>boaviztapi/data/crowdsourcing/gpu_specs.csv</code> carries{' '}
            <strong>no power column of any kind</strong> for any accelerator - it is purely
            manufacturing data (die surface, mass, transport distances). There is no archetype
            power figure to defer to for a GPU component at all.{' '}
            <Ext href="https://github.com/Boavizta/boaviztapi/pull/528">
              Boavizta PR #528
            </Ext>{' '}
            (shipped in the 2.3.0 release this pipeline pins) added exactly this capability: a
            caller-supplied <code className={CODE}>gpu.usage.avg_power</code>, nested under{' '}
            <code className={CODE}>configuration.gpu</code> on the{' '}
            <code className={CODE}>/v1/server/</code> payload, is now folded into the
            response&apos;s <code className={CODE}>impacts.gwp.use</code> alongside the chassis
            figure, in one call - verified empirically against the pinned image and against the{' '}
            <code className={CODE}>ConfigurationServer</code>/<code className={CODE}>GPU</code>/
            <code className={CODE}>Usage</code> schemas in its{' '}
            <code className={CODE}>/openapi.json</code>. The PR&apos;s own description: GPU power
            is added to the modelled server power <em>&quot;because the GPU component has no
            native power model … this avoids inventing a power model from incomplete data.&quot;</em>{' '}
            Supplying a value is the sanctioned path, not a workaround.
          </p>
          <p className="mt-2">
            <strong>Two independent, separately-declared assumptions - never blended into one.</strong>{' '}
            An auditor must be able to see and revise each on its own evidence:
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>Population: accelerator count is slot CAPACITY, not a populated
              count.</strong> Source: <code className={CODE}>class_attributes.gpu_slots</code>,
              falling back to <code className={CODE}>sku_oem_spec.max_gpu_count</code>, then an
              explicit count in the SKU name (&quot;8x B200&quot;), then a curated table of
              fixed-baseboard GPU platforms (for example the Dell PowerEdge XE9680, 8 GPUs), and
              only then an assumed single accelerator. None of these records how many
              accelerators are actually installed - all are capacity.
              Modeling every slot as filled is a <strong>disclosed upper bound</strong>: it
              overstates a partially-populated chassis, replacing today&apos;s understatement
              with a bounded overstatement in the opposite direction.{' '}
              <code className={CODE}>input_provenance.gpu.unitsSource</code> and{' '}
              <code className={CODE}>.populationAssumption</code> record which column was used
              and state the capacity-not-population caveat explicitly. A customer-suppliable
              populated accelerator count (mirroring the PUE org-rung pattern) is the disclosed
              follow-up, not yet built.
            </li>
            <li>
              <strong>Workload: each accelerator is modeled at 50% of its TDP.</strong>{' '}
              Per-accelerator TDPs (below) come from public spec sheets. The 50% derate is{' '}
              <strong>not</strong> calibrated from this fleet&apos;s own measured telemetry -
              every measured GPU-slot asset at decision time belonged to a synthetic-telemetry
              test org, and an 8-GPU chassis reading the same wattage as a GPU-less server is not
              a real signal to calibrate against. Instead, 50% is cited to{' '}
              <strong>BoaviztAPI&apos;s own archetype workload assumption</strong> - its server
              archetype models CPU at <code className={CODE}>time_workload = 50%</code> (visible
              in every <code className={CODE}>raw_response.verbose</code> block,{' '}
              <code className={CODE}>{'{"value": 50, "status": "ARCHETYPE"}'}</code>). Applying
              that same assumption to the component BoaviztAPI does not model keeps one workload
              assumption consistent across CPU and GPU within a single figure, and cites the
              methodology this pipeline already pins rather than an invented constant.{' '}
              <code className={CODE}>input_provenance.gpu.workloadDerateFactor</code> and{' '}
              <code className={CODE}>.workloadAssumption</code> record this separately from the
              population term. (<code className={CODE}>power-cost.ts</code>&apos;s independent{' '}
              <code className={CODE}>0.5 × TDP</code> financial estimate converges with this
              figure - worth noting, not the justification; that path is tuned for a dollar
              estimate and is deliberately kept separate from carbon.)
            </li>
          </ul>
          <p className="mt-2">
            <strong>Per-accelerator TDP table</strong> (decided models only - an accelerator not
            listed here keeps its draw omitted, same understatement as before, disclosed via{' '}
            <code className={CODE}>
              input_provenance.hardwareIdentityDefaults[&apos;gpu.archetype&apos;]
            </code>
            ):
          </p>
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH}>Accelerator</th>
                  <th className={TH}>TDP</th>
                  <th className={TH}>Modeled avg_power (× 0.50)</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className={TD}>NVIDIA L4</td><td className={TD}>72 W</td><td className={TD}>36 W</td></tr>
                <tr><td className={TD}>NVIDIA A10</td><td className={TD}>150 W</td><td className={TD}>75 W</td></tr>
                <tr><td className={TD}>NVIDIA A30</td><td className={TD}>165 W</td><td className={TD}>82.5 W</td></tr>
                <tr><td className={TD}>AMD Instinct MI210</td><td className={TD}>300 W</td><td className={TD}>150 W</td></tr>
                <tr><td className={TD}>NVIDIA A100 PCIe</td><td className={TD}>300 W</td><td className={TD}>150 W</td></tr>
                <tr><td className={TD}>NVIDIA L40S</td><td className={TD}>350 W</td><td className={TD}>175 W</td></tr>
                <tr><td className={TD}>NVIDIA A100 SXM4</td><td className={TD}>400 W</td><td className={TD}>200 W</td></tr>
                <tr><td className={TD}>NVIDIA H200</td><td className={TD}>600 W</td><td className={TD}>300 W</td></tr>
                <tr><td className={TD}>AMD Instinct MI300X</td><td className={TD}>750 W</td><td className={TD}>375 W</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2">
            Notably absent: H100, V100, MI250X, and several others real in the catalog - this is
            a deliberate decision scope, not an oversight; those models keep the prior
            understatement until a future ticket extends the table.
          </p>
          <p className="mt-2">
            <strong>Double-counting guard - the measured path is unaffected.</strong> This
            addition fires <strong>only</strong> when{' '}
            <code className={CODE}>power_basis = &apos;archetype_default&apos;</code>. Measured
            whole-system watts from the BMC already include GPU draw; adding a TDP-derived figure
            on top of a measured wattage would double-count, which is worse than the
            understatement this ticket fixes. <code className={CODE}>computeOneAsset</code> (
            <code className={CODE}>apps/ingestion/src/pipeline/carbon-use-phase.ts</code>) gates
            the addition on this condition directly.
          </p>

          <h3
            id="grid-intensity-national-averages-only"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            Grid intensity - national averages only
          </h3>
          <p className="mt-2">
            The grid emission factor behind every use-phase figure is a{' '}
            <strong>national annual average</strong>. <strong>Sub-national variation is not
            modeled.</strong> For a country with high internal grid variation - notably the
            United States, where EPA eGRID subregion intensities span roughly a{' '}
            <strong>factor of 2-3</strong> - the true location-based figure for a specific
            facility can differ from the reported figure by a similar factor, in either
            direction. The GHG Protocol Scope 2 Guidance prefers sub-national or
            supplier-specific emission factors where available;{' '}
            <strong>these figures do not meet that preference today.</strong> The disclosed
            upgrade path is to swap the national factor table for eGRID subregion factors keyed
            on the asset&apos;s <code className={CODE}>data_center.region_id</code>.
          </p>

          <h3
            id="pue-applied-on-every-scope-2-labeled-surface"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            PUE - applied on every Scope-2-labeled surface
          </h3>
          <p className="mt-2">
            BoaviztAPI&apos;s <code className={CODE}>gwp.use</code> output, and the value stored
            in <code className={CODE}>asset_use_phase_snapshot</code>, are{' '}
            <strong>device-level</strong> - the electricity the computer itself draws. Scope 2
            means <em>purchased</em> electricity, which also includes data-center cooling and
            distribution overhead. A device-level number displayed under a Scope 2 label would be
            systematically <strong>~1.3-1.6x low</strong>.
          </p>
          <p className="mt-2">
            To avoid that, every Scope-2-labeled surface (asset detail, ESG CSV, scenario
            projections) multiplies the stored device-level rate by PUE via one shared function,{' '}
            <code className={CODE}>applyPue()</code>, which refuses (throws) rather than silently
            defaulting a missing PUE to 1.0.
          </p>
          <p className="mt-2">
            PUE is resolved by a ladder: customer-supplied per-data-center PUE → org PUE →
            published industry default. <strong>The per-data-center rung does not exist
            yet</strong> - <code className={CODE}>customer_org.pue</code>{' '}
            shipped the org rung - an org that has
            not set it still resolves{' '}
            <code className={CODE}>pue.source = &apos;industry_default&apos;</code> (currently{' '}
            <strong>1.5</strong>); an org that has set it resolves{' '}
            <code className={CODE}>pue.source = &apos;org&apos;</code>. A defaulted PUE is
            exactly as material a caveat as a defaulted grid factor or defaulted power term, and
            it is treated that way in the accuracy-tier derivation below.{' '}
            <code className={CODE}>customer_org.pue</code>, like the user-supplied PUE on
            financial scenarios, is bounded server-side to <code className={CODE}>[1.0, 3.0]</code>{' '}
            and <strong>rejected</strong>, never clamped, when out of range.
          </p>

          <h3 className="mt-4 text-sm font-semibold text-foreground">
            Every use-phase figure was a screening estimate until an org supplies its PUE
          </h3>
          <p className="mt-2">
            Before per-org PUE data existed, PUE was one of the three tier
            inputs, so <strong>no asset could reach the <code className={CODE}>measured</code>{' '}
            accuracy tier</strong>, regardless of how good its power and location data were. That
            has now changed for any org that sets <code className={CODE}>customer_org.pue</code>:
            combined with qualified telemetry and a real location, such an org&apos;s assets can
            reach <code className={CODE}>measured</code> / <code className={CODE}>reportable</code>.
            Per-data-center PUE is still missing, so an org that
            has not yet set its org-level PUE remains capped below{' '}
            <code className={CODE}>measured</code> exactly as before.
          </p>

          <h3
            id="accuracy-tiers-use-phase"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            Accuracy tiers
          </h3>
          <p className="mt-2">
            Three inputs feed a use-phase figure and each can be measured/real or defaulted:
            power, location, PUE. <strong>The accuracy tier is the weakest of the three</strong>,
            derived at read time from the snapshot&apos;s{' '}
            <code className={CODE}>input_provenance</code> (never stored, so a future rule change
            never strands historical rows):
          </p>
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH}>Tier</th>
                  <th className={TH}>Power</th>
                  <th className={TH}>Location</th>
                  <th className={TH}>PUE</th>
                  <th className={TH}>Meaning for reporting</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={TD}><code className={CODE}>measured</code></td>
                  <td className={TD}>measured</td>
                  <td className={TD}>real</td>
                  <td className={TD}>supplied</td>
                  <td className={TD}>
                    Highest confidence available today. Reachable once an org sets{' '}
                    <code className={CODE}>customer_org.pue</code>; still unreachable
                    for an org that has not (see above).
                  </td>
                </tr>
                <tr>
                  <td className={TD}><code className={CODE}>partial</code></td>
                  <td className={TD}>measured</td>
                  <td className={TD} colSpan={2}>one of location/PUE defaulted</td>
                  <td className={TD}>Power is real; one input is a screening default.</td>
                </tr>
                <tr>
                  <td className={TD}><code className={CODE}>modeled</code></td>
                  <td className={TD}>archetype</td>
                  <td className={TD}>real</td>
                  <td className={TD}>any</td>
                  <td className={TD}>No telemetry; power comes from BoaviztAPI&apos;s archetype model.</td>
                </tr>
                <tr>
                  <td className={TD}><code className={CODE}>indicative</code></td>
                  <td className={TD}>archetype</td>
                  <td className={TD}>defaulted (<code className={CODE}>WOR</code> world-average floor)</td>
                  <td className={TD}>any</td>
                  <td className={TD}>Weakest tier - no telemetry and no resolvable location.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2">
            <strong>Measured-power qualification: ≥1 full calendar month of telemetry at ≥80%
            daily coverage</strong> (both boundaries inclusive). These thresholds are{' '}
            <strong>reasoned, not calibrated</strong> - there is no real power telemetry to
            calibrate against yet, so they are expected to move as telemetry coverage matures.
          </p>

          <h3 id="reporting-eligibility" className="mt-4 text-sm font-semibold text-foreground">
            Reporting eligibility
          </h3>
          <p className="mt-2">
            A stable, additive-only enum lets a downstream pipeline filter without learning tier
            semantics. Nothing is withheld from the ESG CSV export - including the{' '}
            <code className={CODE}>indicative</code> tier:
          </p>
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH}>Value</th>
                  <th className={TH}>Meaning</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className={TD}><code className={CODE}>reportable</code></td><td className={TD}>Measured tier.</td></tr>
                <tr><td className={TD}><code className={CODE}>screening_estimate_default_location</code></td><td className={TD}>Partial - location was defaulted.</td></tr>
                <tr><td className={TD}><code className={CODE}>screening_estimate_default_pue</code></td><td className={TD}>Partial - PUE was the only defaulted term.</td></tr>
                <tr><td className={TD}><code className={CODE}>screening_estimate_default_power</code></td><td className={TD}>Modeled - power came from an archetype.</td></tr>
                <tr><td className={TD}><code className={CODE}>screening_estimate_default_power_and_location</code></td><td className={TD}>Indicative - archetype power and defaulted location.</td></tr>
                <tr><td className={TD}><code className={CODE}>not_computed_pending_first_computation</code></td><td className={TD}>Asset is real and supported, just onboarded - waits for the next monthly pass.</td></tr>
                <tr><td className={TD}><code className={CODE}>not_computed_unsupported_asset_class</code></td><td className={TD}>This asset class never gets a use-phase figure (e.g. bare <code className={CODE}>gpu</code>).</td></tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2">
            A blank cell forces an analyst to either drop the asset - a completeness failure the
            GHG Protocol and ESRS both treat as more serious than an imprecise estimate - or
            invent a number offline with no methodology, bounds, or audit trail. A disclosed
            screening estimate is preferred to both, and blank cells also break sums silently.
            The pipeline <strong>never falls back to an older snapshot</strong> to avoid
            reporting a weak tier - the latest snapshot is always resolved first.
          </p>

          <h3 id="bounds-use-phase" className="mt-4 text-sm font-semibold text-foreground">
            Bounds
          </h3>
          <p className="mt-2">
            <strong>The use-phase min/max bounds mean observed day-to-day power variation
            only.</strong> They are <strong>not</strong> an uncertainty interval and{' '}
            <strong>not</strong> a confidence interval - grid-factor uncertainty and PUE
            uncertainty are not propagated into them at all.
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>Measured path</strong> (
              <code className={CODE}>bounds_basis = &apos;observed_p10_p90&apos;</code>): the
              p10/p90 of this specific asset&apos;s observed daily average power draw across the
              observation window, composed with the same (point) grid factor and PUE used for the
              central estimate.
            </li>
            <li>
              <strong>Archetype path</strong> (
              <code className={CODE}>bounds_basis = &apos;archetype_span&apos;</code>):
              Boavizta&apos;s own min/max for the resolved archetype - not an observed span at
              all.
            </li>
          </ul>
          <p className="mt-2">
            Both bases are recorded per row (<code className={CODE}>bounds_basis</code>), and any
            label or tooltip for these fields must use the exact caveat text in{' '}
            <code className={CODE}>USE_PHASE_BOUNDS_CAVEAT</code>, never invented wording that
            drifts toward &quot;uncertainty&quot; or &quot;confidence.&quot;
          </p>
          <p className="mt-2">
            <strong>Standing rule: report the central estimate, never the minimum</strong>, on
            any surface, for any reason. This mirrors the GHG Protocol Technical Guidance&apos;s
            Appendix B (p158-159), which requires the reported scenario be &quot;representative of
            the range of scenarios, <strong>and not the scenario with the lowest
            emissions.</strong>&quot; The same rule applies to embodied figures.
          </p>
        </section>

        <section>
          <h2 id="fleet-and-lot-aggregation" className="text-base font-semibold text-foreground">
            Fleet and lot aggregation
          </h2>
          <p className="mt-2">
            <strong>Embodied</strong> fleet and lot totals are <strong>sums of per-asset
            estimates</strong>, with lower and upper bounds summed independently. Correlation
            between assets is not modeled, so aggregate bounds are indicative rather than
            statistically rigorous intervals.
          </p>
          <p className="mt-2">
            <strong>Use-phase</strong> fleet and lot totals are sums of{' '}
            <strong>annual rates sampled on different days.</strong> The monthly recompute selects
            each asset on its own roughly-30-day staleness cycle (whichever asset has gone
            longest without a snapshot for the current period gets recomputed first), so the
            underlying per-asset rates in any fleet total are not all &quot;as of&quot; the same
            date - their observation windows are staggered across the month. A fiscal-year total
            assembled by summing or extrapolating from these rates is an{' '}
            <strong>approximation</strong>, not an integral over the year - it is closer to
            &quot;the sum of the most recent annualized rate for each asset&quot; than to
            &quot;the fleet&apos;s actual measured emissions this year.&quot;
          </p>
        </section>

        <section>
          <h2
            id="scenario-forecasts-are-not-inventory-figures"
            className="text-base font-semibold text-foreground"
          >
            Scenario forecasts are not inventory figures
          </h2>
          <p className="mt-2">
            Financial scenarios project a use-phase rate forward, staged by each cohort&apos;s
            action and timing - never a flat <code className={CODE}>rate × horizon</code> for
            every action:
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong><code className={CODE}>sell</code> / <code className={CODE}>itad</code></strong>{' '}
              cohorts stop accruing use-phase carbon at the action month. The operator no longer
              operates (or purchases the electricity for) that hardware afterward - a true zero
              for the remainder, not a gap.
            </li>
            <li>
              <strong><code className={CODE}>refresh</code></strong> cohorts accrue the retiring
              fleet&apos;s rate up to the refresh month. The months from the refresh month to the
              end of the horizon are <strong>explicitly not modeled</strong> - the replacement SKU
              has no owned assets yet, and therefore no{' '}
              <code className={CODE}>asset_use_phase_snapshot</code> row to read a rate from.
              Carrying the old fleet&apos;s rate forward would assert the replacement draws
              exactly what the retired hardware drew (usually the opposite of why an organization
              refreshes); reporting zero would assert the new fleet draws no power at all. Neither
              is honest, so this gap is surfaced as an explicit, named unmodeled remainder
              rather than silently computed.
            </li>
          </ul>
          <p className="mt-2">
            A scenario projection is a <strong>forecast, not an inventory figure</strong>, and is
            branded at the type level (<code className={CODE}>ProjectedUsePhaseKgCo2e</code>) so
            it cannot reach the ESG export without an explicit, reviewable cast.
          </p>
        </section>

        <section>
          <h2
            id="sold-product-reporting-categories-11-and-12"
            className="text-base font-semibold text-foreground"
          >
            Sold-product reporting (Categories 11 and 12)
          </h2>
          <p className="mt-2">
            Scope 3 <strong>Category 11</strong> (use of sold products) and{' '}
            <strong>Category 12</strong> (end-of-life of sold products) shipped{' '}
            <strong>together</strong>, as part of the monthly Scope 3 flow panel. The
            ship-together rule below was the reason they were deferred together and is the reason
            they were released together; it is not retired.
          </p>
          <p className="mt-2">
            <strong>They are not the lifetime, buyer-grounded figures this page previously said
            were blocked.</strong> The blockers named in the deferral (no buyer grid region, no
            buyer duty cycle, no device mass, no phase split inside BoaviztAPI&apos;s{' '}
            <code className={CODE}>gwp.embedded</code>) are all still open. What shipped is a
            narrower, point-of-sale recognition built from data SiliconX already holds. Read the
            caveats below before putting either figure in an inventory.
          </p>

          <h3 id="what-is-computed" className="mt-4 text-sm font-semibold text-foreground">
            What is computed
          </h3>
          <p className="mt-2">
            Both categories are bucketed by the month a{' '}
            <code className={CODE}>marketplace_transaction</code> reached{' '}
            <code className={CODE}>completed</code> (<code className={CODE}>closed_at</code>),
            one point per month, aligned to the same period list the Scope 2 panel plots.
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>Category 11</strong> recognizes each sold asset&apos;s own{' '}
              <strong>annual</strong> use-phase rate (
              <code className={CODE}>v_asset_use_phase_latest.gwp_use_annual_kgco2eq</code> - the
              same per-asset figure the Scope 2 KPI reads) in the month of sale. This follows the
              GHG Protocol convention of recognizing the estimated downstream-use footprint once,
              at the point of sale. SiliconX receives no telemetry from the buyer after ownership
              transfer, so a figure recognized once at sale is the only attribution available.
            </li>
            <li>
              <strong>Category 12</strong> applies the same{' '}
              <code className={CODE}>disposalAdderKgco2e</code> WEEE kernel that Category 5 uses,
              against the sold asset&apos;s own reference unit weight, in the month of sale. It is{' '}
              <strong>not</strong> derived from BoaviztAPI&apos;s{' '}
              <code className={CODE}>gwp.embedded</code>, which still has no phase split.
            </li>
          </ul>
          <p className="mt-2">
            A sold asset with no resolved use-phase rate contributes <strong>0</strong> to
            Category 11 rather than being dropped, so the sale itself is never silently absent
            from the month. A month with no completed sales is a genuine <code className={CODE}>0</code>,
            never conflated with &quot;not derivable&quot;; the whole series is{' '}
            <code className={CODE}>null</code> only when the org has no use-phase periods at all
            to align against.
          </p>

          <h3
            id="caveats-that-survived-the-release"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            Caveats that survived the release
          </h3>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>Category 11 is an annual rate recognized once, not a lifetime figure.</strong>{' '}
              It answers &quot;one year of this asset&apos;s operation, at the point we stopped
              tracking it,&quot; not &quot;everything the buyer will emit running this
              hardware.&quot; A buyer who operates the asset for five years emits roughly five
              times this figure.
            </li>
            <li>
              <strong>Category 11 carries the seller&apos;s grid factor and PUE, not the
              buyer&apos;s.</strong> The rate is the seller-side snapshot, computed against the
              seller&apos;s location and PUE ladder. If the buyer operates in a different country
              or a differently-efficient facility, the true downstream figure differs, and by the
              same factor-of-2-3 magnitudes described in{' '}
              <Int href="#grid-intensity-national-averages-only">Grid intensity</Int> and{' '}
              <Int href="#pue-applied-on-every-scope-2-labeled-surface">PUE</Int>.
            </li>
            <li>
              <strong>Category 12 is a WEEE-factor estimate keyed on a class-level reference unit
              weight</strong>, not this device&apos;s measured mass. SiliconX still holds no
              per-device mass data.
            </li>
            <li>
              <strong>Neither figure inherits a use-phase accuracy tier.</strong> The{' '}
              <Int href="#accuracy-tiers-use-phase">accuracy tiers</Int> and{' '}
              <Int href="#reporting-eligibility">reporting eligibility</Int> enums describe
              per-asset Scope 2 use-phase rows. These monthly Scope 3 flows are aggregates over a
              mix of input bases and are tagged with the coarser dashboard basis vocabulary
              instead.
            </li>
          </ul>

          <h3
            id="the-scope-3-category-matrix-still-renders-sample-values-for-11-and-12"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            The Scope 3 category matrix still renders sample values for 11 and 12
          </h3>
          <p className="mt-2">
            The monthly flow panel is real. The <strong>Scope 3 category matrix&apos;s</strong>{' '}
            Category 11 and 12 cells are <strong>not</strong> - they are still hardcoded sample
            values from <code className={CODE}>environmentalSampleData.ts</code>. Two surfaces on
            the same dashboard therefore show Category 11 and 12 differently, and only the
            monthly flow panel is derived from the org&apos;s own transactions. Do not reconcile
            the two, and do not read a matrix cell as an inventory figure.
          </p>

          <h3
            id="the-double-count-trap-now-live-not-hypothetical"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            The double-count trap - now live, not hypothetical
          </h3>
          <p className="mt-2">
            If an organization reports embodied emissions under Scope 3 Category 1 or 2 when it
            buys an asset, and BoaviztAPI&apos;s <code className={CODE}>gwp.embedded</code>{' '}
            figure already includes end-of-life impact in that number, then also reporting
            Category 12 when that same organization later sells the asset counts end-of-life
            emissions <strong>twice</strong> within one inventory. Shipping Category 12 has made
            this a real exposure rather than a design note: the two figures come from different
            models (BoaviztAPI <code className={CODE}>gwp.embedded</code> versus the WEEE
            kernel), so neither one can detect the overlap, and SiliconX does not net them.
          </p>
          <p className="mt-2">
            <strong>An organization consuming both figures must decide which one carries
            end-of-life for its inventory and exclude the other.</strong> SiliconX does not make
            that determination, for the same reason it makes no other GHG classification
            determination - see{' '}
            <Int href="#classification-is-the-customers-determination">Classification</Int>.
          </p>

          <h3
            id="avoided-emissions-are-never-netted-against-an-inventory-figure"
            className="mt-4 text-sm font-semibold text-foreground"
          >
            Avoided emissions are never netted against an inventory figure
          </h3>
          <p className="mt-2">The GHG Protocol Technical Guidance for Scope 3 forbids this, in two places:</p>
          <blockquote className={QUOTE}>
            &quot;Any claims of avoided emissions related to a company&apos;s sold products{' '}
            <strong>must be reported separately</strong> from the company&apos;s scope 1, scope
            2, and scope 3 inventories.&quot; (p114)
          </blockquote>
          <blockquote className={QUOTE}>
            &quot;Any claims of avoided emissions... <strong>should not be included in, or
            deducted from, the scope 3 inventory</strong>, but may instead be reported
            separately.&quot; (p79)
          </blockquote>
          <p className="mt-2">
            SiliconX&apos;s listing &quot;up to X kg CO2e avoided&quot; claim is reported entirely
            separately from any Scope 2 or Scope 3 inventory figure - there is no field or helper
            function anywhere in the codebase that combines an avoided-claim figure with an
            inventory (embodied or use-phase) figure.
          </p>
        </section>

        <section>
          <h2
            id="dual-lens-embodied-management-view-vs-ghg-inventory-view"
            className="text-base font-semibold text-foreground"
          >
            Dual-lens embodied: management view vs GHG inventory view
          </h2>
          <p className="mt-2">
            The Overview tab&apos;s top panel plots <strong>two structurally different treatments
            of the same embodied figures</strong>, deliberately never summed or reconciled
            against each other:
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>GHG inventory view</strong> (<code className={CODE}>scope3Kgco2e</code>,
              the treemap): full recognition in the acquisition month, per the GHG Protocol
              convention described under{' '}
              <Int href="#classification-is-the-customers-determination">Classification</Int> -
              the entire embodied figure counts once, in the period the asset was acquired.
            </li>
            <li>
              <strong>Management view</strong> (
              <code className={CODE}>amortizedEmbodiedMonthlySeries</code>): the same
              per-asset embodied <em>stock</em> figure spread evenly across a 72-month (
              <code className={CODE}>EMBODIED_AMORTIZATION_DEFAULT_LIFESPAN_MONTHS</code>, or a
              class override) straight-line window, so it plots as a monthly <em>flow</em>{' '}
              alongside the Scope 2 series - useful for a fleet manager comparing &quot;this
              month&apos;s run-rate&quot; across categories, not a substitute inventory figure.
            </li>
          </ul>
          <p className="mt-2">
            Every surface that renders the amortized series carries the locked caption{' '}
            <code className={CODE}>ENV_AMORTIZED_EMBODIED_CAPTION</code> naming it a management
            convention, never invented wording that implies it replaces the inventory figure.
          </p>
          <p className="mt-2"><strong>Amortization window per asset:</strong></p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>Start</strong>: the same acquisition-evidence month the Cat 4 rule below
              establishes - <code className={CODE}>
                LEAST(asset.created_at, MIN(telemetry_daily_agg.day))
              </code>, truncated to month.
            </li>
            <li>
              <strong>End</strong>: <code className={CODE}>
                min(sale/disposal month, evidence month + 71)
              </code>{' '}
              - truncated early if the asset was sold (
              <code className={CODE}>marketplace_transaction.closed_at</code>) or disposed
              (earliest <code className={CODE}>asset_lifecycle_event</code> row transitioning{' '}
              <code className={CODE}>to_state = &apos;disposed&apos;</code>) before the 72-month
              window would otherwise close. An asset that left the org&apos;s fleet stops
              accruing that org&apos;s amortized figure at the exit month - it cannot keep
              contributing to a fleet it left.
            </li>
          </ul>
        </section>

        <section>
          <h2
            id="cat-4-acquisition-evidence-month-rule"
            className="text-base font-semibold text-foreground"
          >
            Cat-4 acquisition-evidence-month rule
          </h2>
          <p className="mt-2">
            Scope 3 Category 4 (upstream transportation) and the amortized-embodied series both
            bucket an asset into a month using the same rule:{' '}
            <code className={CODE}>
              LEAST(asset.created_at, MIN(telemetry_daily_agg.day))
            </code>, truncated to month. Telemetry can predate the asset&apos;s own{' '}
            <code className={CODE}>created_at</code> row in SiliconX - onboarding an
            already-running asset backfills historical telemetry, so the true acquisition
            evidence is whichever signal is older. Using <code className={CODE}>created_at</code>{' '}
            alone would push the bucketing month later than the asset&apos;s earliest real
            evidence of being in service.
          </p>
        </section>

        <section>
          <h2
            id="cat-11-sale-time-freeze-semantics"
            className="text-base font-semibold text-foreground"
          >
            Cat-11 sale-time freeze semantics
          </h2>
          <p className="mt-2">
            Category 11 (use of sold products) recognizes a sold asset&apos;s use-phase rate{' '}
            <strong>as it stood at the sale</strong>, never a snapshot the buyer generates
            afterward. This is enforced at the database layer, not just in application code:
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <code className={CODE}>get_seller_sold_asset_flow_inputs()</code> (migration{' '}
              <code className={CODE}>db/schema/725_seller_sold_asset_flow_inputs.sql</code>) is a
              narrow <code className={CODE}>SECURITY DEFINER</code> function, scoped to exactly
              the five columns Cat 11/12 need, anchored on{' '}
              <code className={CODE}>marketplace_transaction.seller_customer_org_id</code> - the
              durable &quot;who sold this&quot; identity that neither <code className={CODE}>asset</code>
              &apos;s nor <code className={CODE}>v_asset_use_phase_latest</code>&apos;s own
              (current-owner-pinned) RLS policies carry. Isolation is two-layered:{' '}
              <code className={CODE}>EXECUTE</code> is revoked from{' '}
              <code className={CODE}>PUBLIC</code> and granted only to{' '}
              <code className={CODE}>client_readonly</code>, and the function body filters on{' '}
              <code className={CODE}>
                current_setting(&apos;app.client_customer_org&apos;, true)
              </code>{' '}
              (session-scoped, unlike <code className={CODE}>current_user</code>, which{' '}
              <code className={CODE}>SECURITY DEFINER</code> redefines to the function owner).
            </li>
            <li>
              <strong>The freeze bound is <code className={CODE}>period_key</code>, not{' '}
              <code className={CODE}>computed_at</code></strong> (migration{' '}
              <code className={CODE}>
                db/schema/726_seller_sold_asset_flow_inputs_period_bound.sql
              </code>
              ). 725 shipped with <code className={CODE}>computed_at &lt;= closed_at</code>,
              which read as &quot;the latest snapshot that existed as of the sale&quot; - the
              right idea applied to the wrong column. In production the use-phase pipeline
              back-computed every historical period in one August 2026 run, so every
              snapshot&apos;s <code className={CODE}>computed_at</code> landed in August
              regardless of which month it <em>describes</em> -{' '}
              <code className={CODE}>computed_at &lt;= closed_at</code> excluded every snapshot
              for every sale that closed before August, serving 0 for every seller org with real
              completed sales. 726 fixed this by bounding on{' '}
              <code className={CODE}>
                period_key &lt;= date_trunc(&apos;month&apos;, closed_at)
              </code>{' '}
              instead - the latest snapshot <strong>describing</strong> the sale month or an
              earlier one, regardless of when the compute job ran. This is the literal reading of
              the privacy intent the freeze exists for: never disclose a period{' '}
              <em>after</em> the sale, which is a statement about the period a snapshot describes,
              not about when it was computed.
            </li>
          </ul>
          <p className="mt-2">
            This is a load-bearing security decision, not an implementation detail: an earlier
            draft joined <code className={CODE}>v_asset_use_phase_latest</code> directly (the
            asset&apos;s globally latest snapshot), which - because{' '}
            <code className={CODE}>SECURITY DEFINER</code> bypasses exactly the RLS that would
            otherwise stop this - was a cross-tenant leak. The buyer keeps generating new
            snapshots after the sale (their own telemetry, their own compute jobs); reading the{' '}
            <em>latest</em> snapshot instead of the <em>sale-time</em> snapshot would have
            silently tracked the buyer&apos;s live post-sale utilization into the seller&apos;s
            Cat 11 figure forever.
          </p>
        </section>

        <section>
          <h2 id="buyer-lens-conventions" className="text-base font-semibold text-foreground">
            Buyer-lens conventions
          </h2>
          <p className="mt-2">
            The Scope 3 category matrix&apos;s buying-new / buying-used columns split
            the org&apos;s current fleet on <code className={CODE}>asset.previous_asset_id</code>{' '}
            (the ownership-transfer lineage column):
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong><code className={CODE}>previous_asset_id IS NULL</code></strong> -
              &quot;bought new&quot;: onboarded directly, never a platform purchase. Cat 1/2 is
              this asset&apos;s own new-SKU embodied figure.
            </li>
            <li>
              <strong><code className={CODE}>previous_asset_id IS NOT NULL</code></strong> -
              &quot;bought used via SiliconX&quot;: a buyer-owned clone minted by a completed
              platform purchase. Cat 1/2 is <strong>locked at ~0-embodied</strong> - never the raw
              footprint-view figure for these assets, which would
              double-count the original owner&apos;s own Category 1/2 for the same physical
              unit&apos;s manufacturing. Cat 4 instead carries the transport adder (both cohorts)
              plus the refurb adder - 2.5% x the cohort&apos;s SKU-grain embodied sum - for the
              used-buy cohort only.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-base font-semibold text-foreground">
            Reports tab changes
          </h2>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>The full Scope 3 exclusion register moved to the Reports tab.</strong> The
              Overview tab&apos;s Scope 3 category matrix shows only a one-row note at a time
              (dimmed, non-computed categories) rather than the full register - the complete list
              of excluded/not-yet-computed categories, with rationale, now lives on Reports (
              <code className={CODE}>ScopeExclusionRegisterCard</code>). Do not expect the full
              register on Overview.
            </li>
            <li>
              <strong>The intensity (kg/PF-day) KPI was dropped</strong>, not deferred with a
              sample placeholder. Sign-off decision: the denominator (
              <code className={CODE}>sku_oem_spec.fp16_tflops</code>) had coverage on only 35 of
              123 assets at review time and is meaningful only for the GPU cohort, so no serving
              surface computes it and the KPI tile is absent from{' '}
              <code className={CODE}>KpiStrip.tsx</code>, not rendered with a sample value.
            </li>
          </ul>
        </section>

        <section>
          <h2
            id="the-avoided-emissions-claim"
            className="text-base font-semibold text-foreground"
          >
            The avoided-emissions claim
          </h2>
          <p className="mt-2">
            The listing figure rests on a <strong>1:1 displacement assumption</strong>: purchasing
            a refurbished unit instead of a newly manufactured <strong>equivalent-spec</strong>{' '}
            unit. If that substitution holds, the embodied emissions of new manufacture{' '}
            <strong>can be avoided</strong>. This is stated as an assumption, not a measurement -
            SiliconX does not observe what a buyer would otherwise have purchased, and does not
            present avoided emissions as a credit, reduction claim, or counterweight to any other
            emissions. It is also structurally kept separate from any Scope 2 or Scope 3
            inventory figure (see above).
          </p>
        </section>

        <section>
          <h2 className="text-base font-semibold text-foreground">
            The financials/carbon wattage divergence
          </h2>
          <p className="mt-2">
            The financials surface (power-cost forecasting) and the carbon surface (use-phase
            emissions) resolve an asset&apos;s wattage through <strong>two different,
            deliberately separate</strong> code paths, because they answer two different
            questions:
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>Financials</strong> (<code className={CODE}>resolveWatts</code> /{' '}
              <code className={CODE}>annualPowerCostUsd</code>) answers &quot;what will this
              asset cost to run at this location over this period&quot; - it falls back to{' '}
              <code className={CODE}>0.5 × TDP</code> when no telemetry exists, a duty-cycle
              heuristic tuned for a dollar forecast.
            </li>
            <li>
              <strong>Carbon</strong> (this page) answers &quot;what is this asset&apos;s
              emissions rate&quot; - it falls back to BoaviztAPI&apos;s own archetype-derived
              wattage for the resolved server configuration when no telemetry exists, a model
              built for emissions estimation, not cost forecasting.
            </li>
          </ul>
          <p className="mt-2">
            Both paths use the same measured-telemetry rung when it is available; they diverge
            only in what they assume when telemetry is absent. The two numbers are not expected
            to agree, and reconciling them is not a goal - each is correct for the question it
            answers.
          </p>
        </section>

        <section>
          <h2 id="reproducibility" className="text-base font-semibold text-foreground">
            Reproducibility
          </h2>
          <p className="mt-2">
            Every computation - embodied or use-phase - is stored as an{' '}
            <strong>append-only snapshot</strong> containing the exact input payload sent to
            BoaviztAPI, the defaulted or provenance-recorded fields, the pinned methodology
            version, and the full raw API response. Any published figure can be re-derived from
            its snapshot.
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              Embodied: <code className={CODE}>sku_carbon_footprint</code>, latest resolved by{' '}
              <code className={CODE}>v_sku_carbon_footprint_latest</code>.
            </li>
            <li>
              Use-phase: <code className={CODE}>asset_use_phase_snapshot</code>, latest resolved
              by <code className={CODE}>v_asset_use_phase_latest</code> - a{' '}
              <code className={CODE}>security_invoker</code> view, load-bearing for RLS
              correctness since use-phase is customer-grain rather than catalog-grain data.
            </li>
          </ul>
          <p className="mt-2">
            The engineering mechanics - the batch CLIs, CPU/GPU name resolution guards, input
            hashing, period-keyed idempotency, and the pre-run verification gates - are documented
            in <Ext href="/04-ingestion/carbon-computation">Carbon Computation</Ext>.
          </p>

          <h3 id="reconciliation-control" className="mt-4 text-sm font-semibold text-foreground">
            Reconciliation control
          </h3>
          <p className="mt-2">
            Every computed use-phase figure carries an independent cross-check: the stored,
            authoritative BoaviztAPI figure (<code className={CODE}>gwp_use_annual_kgco2eq</code>)
            is re-derived from the same snapshot&apos;s <code className={CODE}>input_payload</code>{' '}
            using the SAME formula BoaviztAPI itself applies (
            <code className={CODE}>useAnnualKgco2e</code>), and the two are compared.{' '}
            <strong>The failure path is: flag -&gt; investigate -&gt; recompute.</strong> A
            relative delta exceeding the tolerance for that snapshot&apos;s{' '}
            <code className={CODE}>power_basis</code> is flagged (amber) on the ops asset-detail
            diagnostic panel for an analyst to investigate; it does not block serving the figure,
            and it does not itself trigger a recompute - an analyst decides whether the underlying
            row needs remediation.
          </p>
          <p className="mt-2">
            <strong>This control measures reproduction fidelity, not physical accuracy - and the
            two must never be conflated.</strong> A passing reconciliation says only that
            SiliconX&apos;s own arithmetic can reproduce BoaviztAPI&apos;s stored output to within
            a fraction of a percent; it says nothing about whether that output is physically
            accurate. Total error in a use-phase figure is dominated by the grid-intensity factor
            (a national annual average, not this asset&apos;s actual grid mix or time-of-use
            profile) and by PUE (routinely a published industry default rather than a measured
            facility figure) - both of which can be off by a factor of 2-3x from a given
            facility&apos;s reality, and <strong>neither of which this control tests.</strong> A
            tight 0.5% reconciliation tolerance sitting next to inputs with that much physical
            uncertainty would read as false precision if the distinction weren&apos;t stated
            plainly.
          </p>
          <p className="mt-2">
            The tolerances are keyed by <code className={CODE}>power_basis</code> because
            BoaviztAPI&apos;s own output rounds differently on each path:
          </p>
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={TH}>Path</th>
                  <th className={TH}>Tolerance</th>
                  <th className={TH}>Population (as of 2026-07-30)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={TD}>
                    Measured (<code className={CODE}>power_basis = telemetry_measured</code>)
                  </td>
                  <td className={TD}>0.5%</td>
                  <td className={TD}>
                    p50 delta 0.006%, max 0.024%, across 58 measured-path snapshots (period
                    2026-06)
                  </td>
                </tr>
                <tr>
                  <td className={TD}>
                    Archetype (<code className={CODE}>power_basis = archetype_default</code>)
                  </td>
                  <td className={TD}>3%</td>
                  <td className={TD}>
                    p50 delta 0.888%, max 0.889%, across 19 archetype-path snapshots (period
                    2026-06)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-2">
            Both populations&apos; residuals are attributed to{' '}
            <strong>BoaviztAPI&apos;s own output rounding</strong> - not a modeling disagreement.
            BoaviztAPI&apos;s <code className={CODE}>gwp.use</code> carries 4 significant figures
            on the measured path but only 2 significant figures on the archetype path. The
            archetype tolerance is <strong>6x looser specifically because of that coarser
            rounding</strong>, not because the archetype path is modeled less rigorously: at the
            ~1,900 kg scale typical of an archetype-path asset, a 2-significant-figure rounding
            step is 100 kg, so rounding alone can reach a 2.6% relative delta on an otherwise
            perfectly healthy archetype-path figure. A single flat tolerance across both paths
            would false-positive on exactly those healthy assets - an unexplained looser bound on
            the weaker path would otherwise read as tuned to pass, which is why this attribution
            is stated explicitly here rather than left implicit.
          </p>
          <p className="mt-2">
            (<code className={CODE}>packages/shared/src/use-phase/use-phase-math.ts</code> -{' '}
            <code className={CODE}>USE_PHASE_RECONCILIATION_TOLERANCE_MEASURED</code> /{' '}
            <code className={CODE}>USE_PHASE_RECONCILIATION_TOLERANCE_ARCHETYPE</code>, the first
            CALIBRATED constants in that module; every other threshold there (
            <code className={CODE}>MIN_OBSERVED_SAMPLES_FOR_BOUNDS</code>) or in{' '}
            <code className={CODE}>accuracy-tier.ts</code> (
            <code className={CODE}>MIN_MEASURED_POWER_WINDOW_DAYS</code>,{' '}
            <code className={CODE}>MIN_MEASURED_POWER_COVERAGE_RATIO</code>) documents itself as
            reasoned, not calibrated - there was no real production data to calibrate any of them
            against at the time they were written.)
          </p>
        </section>

        <section>
          <h2 id="disclaimer" className="text-base font-semibold text-foreground">
            Disclaimer
          </h2>
          <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-700">
            <p>
              <strong>Note:</strong> These figures are <strong>estimates, not audits</strong>.
              They have received <strong>no third-party assurance</strong>. Customers are
              responsible for assessing the suitability of these estimates for their own
              reporting frameworks, including CSRD/ESRS and the GHG Protocol.
            </p>
            <p className="mt-2">
              <strong>Embodied carbon</strong> of purchased hardware commonly maps to GHG Protocol{' '}
              <strong>Scope 3, Category 1 (Purchased Goods and Services)</strong>, or{' '}
              <strong>Category 2 (Capital Goods)</strong> if the customer capitalizes the asset -
              and only in the period of acquisition. This is the common case,{' '}
              <strong>not a determination for your organization</strong>; see{' '}
              <Int href="#classification-is-the-customers-determination">Classification</Int>.
            </p>
            <p className="mt-2">
              <strong>Use-phase (operational) carbon</strong> is labeled{' '}
              <strong>Scope 2, location-based</strong>. No market-based figure is provided. This
              labeling applies <strong>only where the customer directly purchases the
              electricity</strong> for the asset; for colocated or third-party-operated assets,
              use-phase emissions are generally <strong>Scope 3</strong> instead (e.g. Category
              8, Upstream Leased Assets), and the PUE overhead component may not belong in the
              customer&apos;s Scope 2 inventory at all. Every use-phase figure is a{' '}
              <strong>screening estimate</strong> as of this writing - see{' '}
              <Int href="#accuracy-tiers-use-phase">Accuracy tiers</Int> - and grid emission
              factors are national annual averages that do not model sub-national variation (see{' '}
              <Int href="#grid-intensity-national-averages-only">Grid intensity</Int>).
            </p>
            <p className="mt-2">
              <code className={CODE}>gwp_embodied_*</code> and{' '}
              <code className={CODE}>gwp_use_annual_*</code> figures are never summed - they are
              two separately-scoped GHG figures reported in separate columns.
            </p>
          </div>
          <p className="mt-2">
            This disclaimer mirrors the one embedded in the fleet CSV export (
            <code className={CODE}>apps/api/src/routes/client/carbon-export.ts</code>), which
            carries the same points in fuller, multi-line form.
          </p>
        </section>

        <section>
          <h2 id="changelog" className="text-base font-semibold text-foreground">
            Changelog
          </h2>
          <p className="mt-2">
            Append-only substantiation trail for corrections to customer-facing use-phase copy.
            Never backdated, never rewritten in place - each entry quotes the superseded
            string(s) verbatim and states what replaced them and why.
          </p>

          <h3 className="mt-4 text-sm font-semibold text-foreground">
            {/* SIL-485 */}
            2026-08-06 — Environmental Dashboard v1: dual-lens embodied, Cat-4/Cat-11
            bucketing rules, buyer lens, Reports tab moves
          </h3>
          <p className="mt-2">
            Documents the Environmental Dashboard v1 epic (PRs #930, #933, #935-#941, #945, #949,
            #950, #953, #960-#963) and its live-validation follow-ups on top of the Cat 11/12
            basics this page already covered:
          </p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              Added the{' '}
              <Int href="#dual-lens-embodied-management-view-vs-ghg-inventory-view">
                dual-lens embodied
              </Int>{' '}
              section: the 72-month amortized management view vs. the full-recognition GHG
              inventory view, and the locked <code className={CODE}>ENV_AMORTIZED_EMBODIED_CAPTION</code>{' '}
              copy.
            </li>
            <li>
              Added the{' '}
              <Int href="#cat-4-acquisition-evidence-month-rule">
                Cat-4 acquisition-evidence-month rule
              </Int>{' '}
              (<code className={CODE}>LEAST(created_at, first telemetry day)</code>), shared with
              the amortized series.
            </li>
            <li>
              Added{' '}
              <Int href="#cat-11-sale-time-freeze-semantics">
                Cat-11 sale-time freeze semantics
              </Int>
              : migration 725&apos;s <code className={CODE}>SECURITY DEFINER</code> isolation
              design and migration 726&apos;s correction from{' '}
              <code className={CODE}>computed_at &lt;= closed_at</code> (always-zero in prod once
              back-computation existed) to <code className={CODE}>period_key &lt;= sale month</code>.
            </li>
            <li>
              Added <Int href="#buyer-lens-conventions">buyer-lens conventions</Int>: the{' '}
              <code className={CODE}>previous_asset_id</code>-keyed new/used cohort split and the
              used-cohort ~0-embodied Cat 1/2 convention.
            </li>
            <li>
              Documented that the full Scope 3 exclusion register moved to the Reports tab, and
              that the intensity (kg/PF-day) KPI was dropped rather than shipped as a sample
              placeholder.
            </li>
          </ul>

          <h3 className="mt-4 text-sm font-semibold text-foreground">
            {/* SIL-378 */}
            2026-07-30 — Four accuracy-correctness defects on the customer use-phase
            card and CSV disclaimer
          </h3>
          <p className="mt-2">
            Legal review of the customer-facing use-phase surfaces found four defects. All four
            traced to copy that was accurate when written but went stale as{' '}
            <code className={CODE}>power_basis</code>/<code className={CODE}>reporting_eligibility</code>{' '}
            combinations and the PUE input surface evolved underneath it.
          </p>
          <p className="mt-2">
            <strong>Defect A — &quot;no telemetry&quot; claimed for assets that have
            telemetry.</strong> <code className={CODE}>deriveReportingEligibility</code> maps
            BOTH a genuinely telemetry-free asset (
            <code className={CODE}>power_basis = &apos;archetype_default&apos;</code>) AND an
            asset with real telemetry that fails the measured-power coverage/window gate (
            <code className={CODE}>power_basis = &apos;telemetry_measured&apos;</code>, but
            &lt;30 days or &lt;80% daily coverage) onto the same{' '}
            <code className={CODE}>screening_estimate_default_power{'{,_and_location}'}</code>{' '}
            eligibility value. A production asset with 21 of 30 days of real 238W telemetry - fed
            to BoaviztAPI as measured input, failing only the 80% coverage gate at 70% - was told:
          </p>
          <blockquote className={QUOTE}>
            &quot;This asset has no telemetry, so power draw is assumed from a typical
            configuration of this hardware type, which is why the range is wide.&quot;
          </blockquote>
          <p className="mt-2">
            Replaced with copy that branches on <code className={CODE}>power_basis</code> in
            addition to <code className={CODE}>reporting_eligibility</code>. The archetype-basis
            branch keeps a version of the retired sentence (now: &quot;This asset has no power
            telemetry for this period, so power draw is modeled from BoaviztAPI&apos;s generic
            server archetype - one default server profile, not a profile matched to this
            asset&apos;s configuration.&quot;). The demoted-telemetry branch instead states the
            asset&apos;s own measured basis and actual coverage numbers (e.g. &quot;21 of 30
            days&quot;) against the <code className={CODE}>MIN_MEASURED_POWER_COVERAGE_RATIO</code>{' '}
            / <code className={CODE}>MIN_MEASURED_POWER_WINDOW_DAYS</code> thresholds (
            <code className={CODE}>packages/shared/src/use-phase/accuracy-tier.ts</code>), rather
            than denying telemetry exists. The{' '}
            <code className={CODE}>screening_estimate_default_power_and_location</code> sibling
            string received the same fix.
          </p>
          <p className="mt-2">
            <strong>Defect B — the bounds caveat claimed observation where there was none.</strong>{' '}
            Every customer figure rendered:
          </p>
          <blockquote className={QUOTE}>
            &quot;This range reflects observed day-to-day variation in this asset&apos;s power
            draw only. It is not a full uncertainty interval or a confidence interval - the
            grid-intensity factor and PUE carry no modeled uncertainty.&quot;
          </blockquote>
          <p className="mt-2">
            unconditionally, including on <code className={CODE}>bounds_basis = &apos;archetype_span&apos;</code>{' '}
            rows, where the range is BoaviztAPI&apos;s own archetype min/max and carries zero
            observation (19 production assets across 7 orgs at the time of writing). Added{' '}
            <code className={CODE}>USE_PHASE_BOUNDS_CAVEAT_ARCHETYPE</code> (selected on{' '}
            <code className={CODE}>boundsBasis</code>, alongside the unchanged, still-verbatim{' '}
            <code className={CODE}>USE_PHASE_BOUNDS_CAVEAT</code> on the{' '}
            <code className={CODE}>observed_p10_p90</code> path):
          </p>
          <blockquote className={QUOTE}>
            &quot;This range is BoaviztAPI&apos;s own minimum-maximum for the generic server
            archetype used in place of measured power. It is not observed variation in this
            asset&apos;s power draw, and it is not an uncertainty interval or a confidence
            interval - the grid-intensity factor and PUE carry no modeled uncertainty.&quot;
          </blockquote>
          <p className="mt-2">
            <strong>Defect C — the CSV disclaimer carried Defect A&apos;s flaw, in a downloadable
            artifact.</strong> The fleet CSV export&apos;s screening-estimate disclaimer line (
            <code className={CODE}>apps/api/src/routes/client/carbon-export.ts</code>) said:
          </p>
          <blockquote className={QUOTE}>
            &quot;Where power, location, or PUE inputs were unavailable, Boavizta archetype
            defaults were used - see the use_phase_reporting_eligibility column for which.&quot;
          </blockquote>
          <p className="mt-2">
            False for the demoted-telemetry class: power was not unavailable, and no archetype
            default was used - only a lower accuracy grade. Replaced with:
          </p>
          <blockquote className={QUOTE}>
            &quot;Where power, location, or PUE inputs were unavailable, or where telemetry
            coverage was insufficient to grade a figure as measured, Boavizta archetype defaults
            or a lower accuracy grade were applied - see the{' '}
            <code className={CODE}>use_phase_reporting_eligibility</code> column.&quot;
          </blockquote>
          <p className="mt-2">
            The same paragraph also carried a now-false absolute - &quot;No asset in this export
            qualifies for the highest accuracy tier, because per-facility PUE data is not
            available for any asset&quot; - which the org-level PUE input surface (
            <code className={CODE}>PATCH /api/client/me/pue</code>,{' '}
            <code className={CODE}>customer_org.pue</code>, migration 708) made false; that
            clause was dropped in the same edit rather than left to contradict the corrected
            sentence beside it.
          </p>
          <p className="mt-2">
            <strong>Defect D — the PUE remedy pointed at a control that didn&apos;t exist, and
            then did.</strong> <code className={CODE}>screening_estimate_default_pue</code>&apos;s
            remedy originally said &quot;Supply your facility&apos;s PUE to replace the industry
            default,&quot; written before any PUE input existed anywhere in the product. A later
            release shipped that control (an admin-gated &quot;Facility efficiency (PUE)&quot; section on
            the customer Profile page, backed by <code className={CODE}>PATCH</code>/
            <code className={CODE}>DELETE /api/client/me/pue</code>). The remedy remains
            imperative and was reworded to name where the control actually lives and that it is
            admin-gated: &quot;An org admin can supply your facility&apos;s PUE from the Profile
            page to replace the industry default.&quot;
          </p>
          <p className="mt-2">
            <strong>Also added</strong> (not a correction of prior copy, a new requirement): an
            always-rendered screening-estimate line on every computed customer use-phase figure,
            adjacent to the value and tier badge rather than buried in the eligibility-specific
            reason string:
          </p>
          <blockquote className={QUOTE}>
            &quot;Screening estimate. Assess suitability for your own reporting before using this
            figure in an inventory.&quot;
          </blockquote>
          <p className="mt-2">
            An earlier draft of this line asserted &quot;no asset reaches the highest accuracy
            grade, because per-facility PUE data does not yet exist&quot; - also made false by
            the org-level PUE input surface - and was deliberately not included.
          </p>

          <h3 className="mt-4 text-sm font-semibold text-foreground">
            {/* SIL-385 */}
            2026-07-30 — One document-level bounds caveat cannot be true for both
            observed and archetype export rows
          </h3>
          <p className="mt-2">
            The fleet CSV export&apos;s disclaimer preamble (
            <code className={CODE}>apps/api/src/routes/client/carbon-export.ts</code>) rendered
            the bounds-variation caveat ONCE, document-level, before any per-row data, inside the
            &quot;DO NOT SUM&quot; line:
          </p>
          <blockquote className={QUOTE}>
            &quot;DO NOT SUM: gwp_embodied_* and gwp_use_annual_* are two separate,
            independently-scoped GHG figures reported in separate columns (see the classification
            note below) - never sum them into a single total. This range reflects observed
            day-to-day variation in this asset&apos;s power draw only. It is not a full
            uncertainty interval or a confidence interval - the grid-intensity factor and PUE
            carry no modeled uncertainty.&quot;
          </blockquote>
          <p className="mt-2">
            A single export row-set can contain BOTH{' '}
            <code className={CODE}>use_phase_bounds_basis = observed_p10_p90</code> rows (where
            the trailing sentence is true - the range really is that asset&apos;s own observed
            power variation) AND <code className={CODE}>bounds_basis = archetype_span</code> rows
            (where the range is BoaviztAPI&apos;s archetype min/max, containing zero observation
            - see the entry above on the same bounds-caveat defect). 58 observed vs. 19 archetype
            rows in production at the time of filing. The blanket sentence was a false statement
            of fact for every archetype row in the export, the CSV sibling of the exact defect
            fixed on the customer asset card.
          </p>
          <p className="mt-2">
            This was NOT a case of a third caveat variant being warranted (three near-identical
            legal strings is the drift problem <code className={CODE}>USE_PHASE_BOUNDS_CAVEAT</code>
            /<code className={CODE}>USE_PHASE_BOUNDS_CAVEAT_ARCHETYPE</code> exist to prevent) and
            NOT a case for a new CSV column (<code className={CODE}>CARBON_EXPORT_COLUMNS</code>{' '}
            is additive-only, published, customer-loaded-into-ESG-tooling contract - the per-row{' '}
            <code className={CODE}>use_phase_bounds_basis</code> column the caveat needed to
            point at already exists). The preamble was split into two <code className={CODE}>#</code>{' '}
            lines and reworded to name BOTH bases and point the reader at the existing{' '}
            <code className={CODE}>use_phase_bounds_basis</code> column, quoting BOTH{' '}
            <code className={CODE}>dto/use-phase.ts</code> constants verbatim rather than
            re-authoring either:
          </p>
          <blockquote className={QUOTE}>
            &quot;DO NOT SUM: gwp_embodied_* and gwp_use_annual_* are two separate,
            independently-scoped GHG figures reported in separate columns (see the classification
            note below) - never sum them into a single total.&quot;
          </blockquote>
          <blockquote className={QUOTE}>
            &quot;USE-PHASE BOUNDS BASIS VARIES BY ROW: the gwp_use_annual_min/gwp_use_annual_max
            range is NOT computed the same way for every row in this file - check the
            use_phase_bounds_basis column for each row. Where use_phase_bounds_basis =
            observed_p10_p90: [<code className={CODE}>USE_PHASE_BOUNDS_CAVEAT</code>, unchanged,
            verbatim]. Where use_phase_bounds_basis = archetype_span: [
            <code className={CODE}>USE_PHASE_BOUNDS_CAVEAT_ARCHETYPE</code>, unchanged,
            verbatim].&quot;
          </blockquote>
          <p className="mt-2">
            <code className={CODE}>USE_PHASE_BOUNDS_CAVEAT</code> itself was not touched - four
            other surfaces reference it verbatim and a sibling test asserts it byte-for-byte.
          </p>

          <h3 className="mt-4 text-sm font-semibold text-foreground">
            {/* SIL-383/388/389 */}
            2026-07-30 — Accelerator use-phase power on the archetype path
          </h3>
          <p className="mt-2">
            {/* SIL-376 */}
            Fixes the defect quantified earlier: all 19 no-telemetry GPU-server assets in
            production resolved to the same 581 W archetype figure because{' '}
            <code className={CODE}>/v1/server/</code>&apos;s archetype model has no GPU draw
            component at all - accelerator draw was entirely absent from the use-phase figure, an
            indicative 3-4x understatement for a populated unit on a Scope-2-labeled compliance
            surface.
          </p>
          <p className="mt-2"><strong>Decided (owner sign-off 2026-07-30):</strong></p>
          <ul className="ml-5 mt-2 list-disc space-y-1">
            <li>
              <strong>Accelerator count = slot capacity</strong> (
              <code className={CODE}>class_attributes.gpu_slots</code> {'→'}{' '}
              <code className={CODE}>sku_oem_spec.max_gpu_count</code>), disclosed explicitly as
              an upper bound, not a populated count. Trades today&apos;s understatement
              for a disclosed, bounded overstatement in the opposite direction - the alternative
              considered and rejected was withholding a figure entirely until a populated-count
              input surface exists, which was rejected as leaving a known 3-4x understatement
              live.
            </li>
            <li>
              <strong>Per-accelerator avg_power = TDP × 0.50</strong>, the derate cited to
              BoaviztAPI&apos;s own archetype CPU workload assumption (
              <code className={CODE}>time_workload = 50%</code>), not to this fleet&apos;s own
              telemetry - every measured GPU-slot asset at decision time belonged to a
              synthetic-telemetry test org.
            </li>
            <li>
              These are recorded as <strong>two separate, independently-revisable terms</strong>{' '}
              in <code className={CODE}>input_provenance.gpu</code> (
              <code className={CODE}>unitsSource</code>/<code className={CODE}>populationAssumption</code>{' '}
              vs. <code className={CODE}>tdpWatts</code>/<code className={CODE}>workloadDerateFactor</code>
              /<code className={CODE}>avgPowerPerUnitWatts</code>/<code className={CODE}>workloadAssumption</code>{' '}
              ) - never blended into one note.
            </li>
            <li>
              Applied <strong>only</strong> on{' '}
              <code className={CODE}>power_basis = &apos;archetype_default&apos;</code> - measured
              whole-system watts already include GPU draw, so applying this on the measured path
              would double-count.
            </li>
          </ul>
          <p className="mt-2">
            See{' '}
            <Int href="#accelerator-power-on-the-archetype-path-sil-388-389-2026-07-30">
              Accelerator power on the archetype path
            </Int>{' '}
            above for the full argument, including why a per-component TDP figure is a different
            claim from the whole-<em>system</em> TDP rung this document has always rejected, and
            the citation to{' '}
            <Ext href="https://github.com/Boavizta/boaviztapi/pull/528">Boavizta PR #528</Ext>{' '}
            that makes a caller-supplied <code className={CODE}>gpu.usage.avg_power</code> the
            upstream-sanctioned design rather than an invented power model.
          </p>
          <p className="mt-2">
            <code className={CODE}>
              input_provenance.hardwareIdentityDefaults[&apos;gpu.archetype&apos;]
            </code>{' '}
            previously read:
          </p>
          <blockquote className={QUOTE}>
            &quot;DEFAULT (no telemetry) — /v1/server/&apos;s archetype model has no GPU draw
            component, so this archetype-default watt figure (and the resulting gwp.use)
            SUBSTANTIALLY UNDERSTATES a real GPU chassis. ...&quot;
          </blockquote>
          <p className="mt-2">
            unconditionally for every archetype-path GPU server. That claim is only still true
            for accelerator models absent from the TDP table above (the decided scope
            is nine models); for a covered model it now reads &quot;MODELED (no telemetry) —
            accelerator draw is now included via a TDP-derived avg_power...&quot; and points at{' '}
            <code className={CODE}>input_provenance.gpu</code> for the two assumptions behind the
            figure.
          </p>
        </section>
      </Card>
    </div>
  );
}
